import { Link, useNavigate } from 'react-router-dom';

import useProtectAction from '@/hooks/useProtectAction';
import useTranslations from '@/i18n/useTranslations';
import FollowButton from '@/shared-components/buttons/components/FollowButton';
import ArtworkImage from '@/shared-components/cards/components/ArtworkImage';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import Utils from '@/utils';
import { formatCurrencyAmount } from '@/utils/currency.utils';

import './AlertFrame.css';

function UnknownNotification() {
  return <></>;
}

function UnavailableNotification({ elapsed }) {
  const t = useTranslations('common.notifications.frames');

  return (
    <div className="notification-generic notification-generic--unavailable">
      <div className="nf-body">
        <p className="text">
          <span className="msg">{t('activity_unavailable')}</span>
          <span className="elapsed"> {elapsed}</span>
        </p>
      </div>
    </div>
  );
}

function isRecord(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function hasRenderableArtwork(artwork) {
  return isRecord(artwork)
    && typeof artwork.slug === 'string'
    && artwork.slug.length > 0
    && isRecord(artwork.artwork_about)
    && isRecord(artwork.artwork_media?.artwork_main_picture);
}

const NotificationDataValidators = Object.freeze({
  artwork: hasRenderableArtwork,
  artlist: isRecord,
  artworkThought: isRecord,
  userInteraction: isRecord,
  userSecondInteraction: isRecord
});

function hasRequiredNotificationData(data, requirements = []) {
  return requirements.every((field) => NotificationDataValidators[field]?.(data[field]));
}

function resolveNotificationUserType(user) {
  const userType = user?.__t || user?.user_type;
  return typeof userType === 'string' ? userType.toLowerCase() : 'collector';
}

function buildChannelRoute(channel, channelOwner) {
  const ownerUsername = channelOwner?.user_username;
  const channelId = channel?._id || channel?.id;
  if (!ownerUsername || !channelId) {
    return null;
  }
  return `/channel/${ownerUsername}/${channelId}`;
}

function buildChannelMessagePreview(channelMessage) {
  if (!channelMessage || typeof channelMessage !== 'object') {
    return '';
  }
  if (channelMessage.message_type === 'sticker' || channelMessage.message_sticker) {
    return 'Sticker';
  }
  if (typeof channelMessage.text === 'string' && channelMessage.text.trim()) {
    const trimmed = channelMessage.text.trim();
    return trimmed.length > 160 ? `${trimmed.slice(0, 157)}...` : trimmed;
  }
  if (channelMessage.message_type === 'image' || channelMessage.message_type === 'video') {
    return 'Attachment';
  }
  return '';
}

function GenericNotification({
  children,
  artworkImageId,
  userImageId,
  userType,
  userName,
  slug,
  rightImageUrl = '',
  onClick = null
}) {
  const privateActionHandler = useProtectAction({ typeOfProtection: 'USER_ACCOUNT_REQUIRED' });
  const navigate = useNavigate();

  const rightLink = rightImageUrl ? rightImageUrl : `/artwork/${slug}`;

  const handleArtworkImageClick = (e) => {
    e.stopPropagation();
    navigate(rightLink, { state: { from: true } });
  };

  return (
    <div
      className="notification-generic"
      onClick={(e) => {
        e.stopPropagation();
        onClick && onClick();
      }}
    >
      <div className="image">
        <UserProfileImage
          className="notification-generic__profile-image"
          image={userImageId}
          typeOfProfile={userType}
          onClick={(e) => {
            e.stopPropagation();
            privateActionHandler(() => navigate(`/user/${userName}`));
          }}
        />
      </div>

      <div className="nf-body">{children}</div>

      <div className="thought-image">
        <Link to={rightLink} state={{ from: true }} onClick={handleArtworkImageClick}>
          <ArtworkImage
            image={artworkImageId}
            imageClassName="notification-image"
            imageTemplateClassName="notification-image"
            onClick={(e) => {
              e.stopPropagation();
              navigate(rightLink, { state: { from: true } });
            }}
            forceSmaller={300}
          />
        </Link>
      </div>
    </div>
  );
}

/* ---------- Tipos de notificación ---------- */

function ArtlistAddition({ artlist, userInteraction: user, elapsed, artwork }) {
  const t = useTranslations('common.notifications.frames');
  const {
    artwork_about: { artwork_title },
    artwork_media: {
      artwork_main_picture: { image_id }
    }
  } = artwork;
  const { artlist_title, _id } = artlist;
  const navigate = useNavigate();

  return (
    <GenericNotification
      userType={resolveNotificationUserType(user)}
      slug={artwork.slug}
      userImageId={user.user_image?.image_id}
      artworkImageId={image_id}
      userName={user.user_username}
      onClick={() => navigate(`/artlist/${_id}`, { state: { from: true } })}
    >
      <p className="text">
        <span className="msg">
          <Link
            className="user-link"
            to={`/user/${user.user_username}`}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/user/${user.user_username}`);
            }}
            state={{ from: true }}
          >
            {user.user_username}
          </Link>{' '}
          {t('artlist_addition.message', {
            artworkTitle: artwork_title,
            artlistTitle: artlist_title
          })}
        </span>
        <span className="elapsed"> {elapsed}</span>
      </p>
    </GenericNotification>
  );
}

function ArtlistLike({ artlist, userInteraction: user, elapsed }) {
  const t = useTranslations('common.notifications.frames');
  const { artlist_title, _id } = artlist;
  const artlistArtworks = Array.isArray(artlist.artlist_artworks)
    ? artlist.artlist_artworks
    : [];
  const image_id = artlistArtworks.length
    ? artlistArtworks[artlistArtworks.length - 1]?.artwork_media?.artwork_main_picture?.image_id
    : '';
  const navigate = useNavigate();

  return (
    <GenericNotification
      userType={resolveNotificationUserType(user)}
      userImageId={user.user_image?.image_id}
      artworkImageId={image_id}
      userName={user.user_username}
      rightImageUrl={`/artlist/${_id}`}
      onClick={() => navigate(`/artlist/${_id}`, { state: { from: true } })}
    >
      <p className="text">
        <span className="msg">
          <Link
            className="user-link"
            to={`/user/${user.user_username}`}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/user/${user.user_username}`);
            }}
            state={{ from: true }}
          >
            {user.user_username}
          </Link>{' '}
          {t('artlist_like.message', {
            artlistTitle: artlist_title
          })}
        </span>
        <span className="elapsed"> {elapsed}</span>
      </p>
    </GenericNotification>
  );
}

function ThoughtReplyInfo({
  artwork,
  userInteraction: user,
  artworkThought,
  elapsed,
  artworkThoughtParentId,
  userSecondInteraction: thoughtCreator
}) {
  const t = useTranslations('common.notifications.frames');
  const {
    slug,
    artwork_media: {
      artwork_main_picture: { image_id }
    }
  } = artwork;
  const { thought_message, _id: thought_id } = artworkThought;
  const navigate = useNavigate();

  return (
    <GenericNotification
      userType={resolveNotificationUserType(user)}
      rightImageUrl={`/artwork/${slug}?thought=${artworkThoughtParentId || thought_id}`}
      userImageId={user.user_image?.image_id}
      artworkImageId={image_id}
      userName={user.user_username}
      onClick={() =>
        navigate(`/artwork/${slug}?thought=${artworkThoughtParentId}`, {
          state: { from: true, reply: true }
        })
      }
    >
      <p className="text">
        <span className="msg">
          <Link
            className="user-link"
            to={`/user/${user.user_username}`}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/user/${user.user_username}`);
            }}
            state={{ from: true }}
          >
            {user.user_username}
          </Link>{' '}
          {t('thought_reply_info.message', {
            thoughtCreator: thoughtCreator.user_username,
            thoughtMessage: thought_message
          })}
        </span>
        <span className="elapsed"> {elapsed}</span>
      </p>
    </GenericNotification>
  );
}

function ThoughtReply({ artwork, userInteraction: user, artworkThought, artworkThoughtParentId, elapsed }) {
  const t = useTranslations('common.notifications.frames');
  const {
    slug,
    artwork_media: {
      artwork_main_picture: { image_id }
    }
  } = artwork;
  const { thought_message, _id: thought_id } = artworkThought;
  const navigate = useNavigate();

  return (
    <GenericNotification
      userType={resolveNotificationUserType(user)}
      rightImageUrl={`/artwork/${slug}?thought=${artworkThoughtParentId || thought_id}`}
      userImageId={user.user_image?.image_id}
      artworkImageId={image_id}
      userName={user.user_username}
      onClick={() =>
        navigate(`/artwork/${slug}?thought=${artworkThoughtParentId}`, {
          state: { from: true, reply: true }
        })
      }
    >
      <p className="text">
        <span className="msg">
          <Link
            className="user-link"
            to={`/user/${user.user_username}`}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/user/${user.user_username}`);
            }}
            state={{ from: true }}
          >
            {user.user_username}
          </Link>{' '}
          {t('thought_reply.message', {
            thoughtMessage: thought_message
          })}
        </span>
        <span className="elapsed"> {elapsed}</span>
      </p>
    </GenericNotification>
  );
}

function ThoughtReplyLike({ artwork, userInteraction: user, artworkThought, artworkThoughtParentId, elapsed }) {
  const t = useTranslations('common.notifications.frames');
  const {
    slug,
    artwork_media: {
      artwork_main_picture: { image_id }
    }
  } = artwork;
  const { thought_message, _id: thought_id } = artworkThought;
  const navigate = useNavigate();

  return (
    <GenericNotification
      userType={resolveNotificationUserType(user)}
      rightImageUrl={`/artwork/${slug}?thought=${artworkThoughtParentId || thought_id}`}
      userImageId={user.user_image?.image_id}
      artworkImageId={image_id}
      userName={user.user_username}
      onClick={() =>
        navigate(`/artwork/${slug}?thought=${artworkThoughtParentId}`, {
          state: { from: true, reply: true }
        })
      }
    >
      <p className="text">
        <span className="msg">
          <Link
            className="user-link"
            to={`/user/${user.user_username}`}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/user/${user.user_username}`);
            }}
            state={{ from: true }}
          >
            {user.user_username}
          </Link>{' '}
          {t('thought_reply_like.message', {
            thoughtMessage: thought_message
          })}
        </span>
        <span className="elapsed"> {elapsed}</span>
      </p>
    </GenericNotification>
  );
}

function RatingArtwork({ artwork, userInteraction: user, elapsed }) {
  const t = useTranslations('common.notifications.frames');
  const {
    slug,
    artwork_about: { artwork_title },
    artwork_media: {
      artwork_main_picture: { image_id }
    }
  } = artwork;
  const navigate = useNavigate();

  return (
    <GenericNotification
      userType={resolveNotificationUserType(user)}
      rightImageUrl={`/artwork/${slug}?rate=true`}
      userImageId={user.user_image?.image_id}
      artworkImageId={image_id}
      userName={user.user_username}
      onClick={() => navigate(`/artwork/${slug}?rate=true`, { state: { from: true } })}
    >
      <p className="text">
        <span className="msg">
          <Link
            className="user-link"
            to={`/user/${user.user_username}`}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/user/${user.user_username}`);
            }}
            state={{ from: true }}
          >
            {user.user_username}
          </Link>{' '}
          {t('rating_artwork.prefix')}{' '}
          <span className="artwork-title">{artwork_title}</span>
        </span>
        <span className="elapsed"> {elapsed}</span>
      </p>
    </GenericNotification>
  );
}

function ArtworkThought({ artwork, userInteraction: user, artworkThought, elapsed }) {
  const t = useTranslations('common.notifications.frames');
  const {
    slug,
    artwork_media: {
      artwork_main_picture: { image_id }
    }
  } = artwork;
  const { thought_message, _id: thought_id } = artworkThought;
  const navigate = useNavigate();

  return (
    <GenericNotification
      userType={resolveNotificationUserType(user)}
      rightImageUrl={`/artwork/${slug}?thought=${thought_id}`}
      userImageId={user.user_image?.image_id}
      artworkImageId={image_id}
      userName={user.user_username}
      onClick={() => navigate(`/artwork/${slug}?thought=${thought_id}`, { state: { from: true } })}
    >
      <p className="text">
        <span className="msg">
          <Link
            className="user-link"
            to={`/user/${user.user_username}`}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/user/${user.user_username}`);
            }}
            state={{ from: true }}
          >
            {user.user_username}
          </Link>{' '}
          {t('artwork_thought.message', {
            thoughtMessage: thought_message
          })}
        </span>
        <span className="elapsed"> {elapsed}</span>
      </p>
    </GenericNotification>
  );
}

function UserMention({ artwork, userInteraction: user, elapsed, artworkThought }) {
  const t = useTranslations('common.notifications.frames');
  const {
    slug,
    artwork_about: { artwork_title },
    artwork_media: {
      artwork_main_picture: { image_id }
    }
  } = artwork;
  const { _id: thought_id } = artworkThought;
  const navigate = useNavigate();

  return (
    <GenericNotification
      userType={resolveNotificationUserType(user)}
      slug={slug}
      userImageId={user.user_image?.image_id}
      artworkImageId={image_id}
      userName={user.user_username}
      onClick={() => navigate(`/artwork/${slug}?thought=${thought_id}`, { state: { from: true } })}
    >
      <p className="text">
        <span className="msg">
          <Link
            className="user-link"
            to={`/user/${user.user_username}`}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/user/${user.user_username}`);
            }}
            state={{ from: true }}
          >
            {user.user_username}
          </Link>{' '}
          {t('user_mention.prefix')}{' '}
          <span className="artwork-title">{artwork_title}</span>
        </span>
        <span className="elapsed"> {elapsed}</span>
      </p>
    </GenericNotification>
  );
}

function ArtworkThoughtLike({ artwork, userInteraction: user, artworkThought, elapsed }) {
  const t = useTranslations('common.notifications.frames');
  const {
    slug,
    artwork_media: {
      artwork_main_picture: { image_id }
    }
  } = artwork;
  const { thought_message, _id: thought_id } = artworkThought;
  const navigate = useNavigate();

  return (
    <GenericNotification
      userType={resolveNotificationUserType(user)}
      slug={slug}
      userImageId={user.user_image?.image_id}
      rightImageUrl={`/artwork/${slug}?thought=${thought_id}`}
      artworkImageId={image_id}
      userName={user.user_username}
      onClick={() => navigate(`/artwork/${slug}?thought=${thought_id}`, { state: { from: true } })}
    >
      <p className="text">
        <span className="msg">
          <Link
            className="user-link"
            to={`/user/${user.user_username}`}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/user/${user.user_username}`);
            }}
            state={{ from: true }}
          >
            {user.user_username}
          </Link>{' '}
          {t('artwork_thought_like.message', {
            thoughtMessage: thought_message
          })}
        </span>
        <span className="elapsed"> {elapsed}</span>
      </p>
    </GenericNotification>
  );
}

function ArtworkLike({ artwork, userInteraction: user, elapsed }) {
  const t = useTranslations('common.notifications.frames');
  const {
    slug,
    artwork_about: { artwork_title },
    artwork_media: {
      artwork_main_picture: { image_id }
    }
  } = artwork;
  const navigate = useNavigate();

  return (
    <GenericNotification
      userType={resolveNotificationUserType(user)}
      slug={slug}
      userImageId={user.user_image?.image_id}
      artworkImageId={image_id}
      userName={user.user_username}
      onClick={() => navigate(`/artwork/${slug}`, { state: { from: true } })}
    >
      <p className="text">
        <span className="msg">
          <Link
            className="user-link"
            to={`/user/${user.user_username}`}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/user/${user.user_username}`);
            }}
            state={{ from: true }}
          >
            {user.user_username}
          </Link>{' '}
          {t('artwork_like.prefix')}{' '}
          <span className="artwork-title">{artwork_title}</span>
        </span>
        <span className="elapsed"> {elapsed}</span>
      </p>
    </GenericNotification>
  );
}

function UserFollowing({ userInteraction: user, followed, elapsed }) {
  const t = useTranslations('common.notifications.frames');
  const navigate = useNavigate();

  return (
    <div className="notification-generic" onClick={() => navigate(`/user/${user.user_username}`)}>
      <div className="image">
        <UserProfileImage
          className="notification-generic__profile-image"
          image={user.user_image?.image_id}
          typeOfProfile={resolveNotificationUserType(user)}
          onClick={() => navigate(`/user/${user.user_username}`)}
        />
      </div>
      <div className="nf-body">
        <p className="text">
          <span className="msg">
            <Link
              className="user-link"
              to={`/user/${user.user_username}`}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/user/${user.user_username}`);
              }}
              state={{ from: true }}
            >
              {user.user_username}
            </Link>{' '}
            {t('user_following.message')}
          </span>
          <span className="elapsed"> {elapsed}</span>
        </p>
      </div>
      <div className="thought-image">
        <FollowButton
          userId={user._id}
          imFollowing={followed}
          isFollowingMe={true}
          className="render-inspiring-result__follow-button"
          userName={user.user_username} />
      </div>
    </div>
  );
}

function DirectChatMessage({ userInteraction: user, elapsed }) {
  const t = useTranslations('common.notifications.frames');
  const navigate = useNavigate();
  const userName = user?.user_username;

  return (
    <div
      className="notification-generic"
      onClick={() => userName && navigate(`/chat/dm/${userName}`, { state: { from: true } })}
    >
      <div className="image">
        <UserProfileImage
          className="notification-generic__profile-image"
          image={user?.user_image?.image_id}
          typeOfProfile={resolveNotificationUserType(user)}
          onClick={() => userName && navigate(`/user/${userName}`)}
        />
      </div>
      <div className="nf-body">
        <p className="text">
          <span className="msg">
            <Link
              className="user-link"
              to={`/user/${userName}`}
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/user/${userName}`);
              }}
              state={{ from: true }}
            >
              {userName}
            </Link>{' '}
            {t('direct_chat_message.message')}
          </span>
          <span className="elapsed"> {elapsed}</span>
        </p>
      </div>
    </div>
  );
}

const OPERATIONAL_NOTIFICATION_KEYS = Object.freeze({
  post_like: 'post_like',
  post_comment: 'post_comment',
  post_mention: 'post_mention',
  channel_owner_message: 'channel_owner_message',
  channel_joined_message: 'channel_joined_message',
  channel_mention: 'channel_mention',
  order_paid: 'order_paid',
  shipping_reminder: 'shipping_reminder',
  order_message: 'order_message',
  gallery_review: 'gallery_review',
  artwork_claimed: 'artwork_claimed',
  order_shipped: 'order_shipped',
  order_delivered: 'order_delivered',
  order_cancelled: 'order_cancelled',
  order_refunded: 'order_refunded',
  artwork_original_sold: 'artwork_original_sold',
  limited_edition_sold: 'limited_edition_sold',
  gallery_artwork_marked_sold: 'gallery_artwork_marked_sold',
  gallery_artwork_added: 'gallery_artwork_added',
  gallery_artwork_removed: 'gallery_artwork_removed',
  follower_artwork_published: 'follower_artwork_published',
  follower_post_published: 'follower_post_published',
  follower_artlist_published: 'follower_artlist_published',
  follower_collector_thought_published: 'follower_collector_thought_published',
  follower_collector_rating_published: 'follower_collector_rating_published',
  follower_collector_purchased_artwork: 'follower_collector_purchased_artwork'
});

function getOperationalNotificationRoute({ type, post, order, channel, channelOwner, artwork, artlist }) {
  if ((type === 'artwork_original_sold' || type === 'limited_edition_sold') && artwork?.slug) {
    return `/artwork/${artwork.slug}`;
  }
  if (order?.order_number) {
    return type === 'order_message'
      ? `/order/${order.order_number}?conversation=1`
      : `/order/${order.order_number}`;
  }
  if (post?._id) {
    return post.post_type === 'article' ? `/article/${post._id}` : `/post/${post._id}`;
  }
  if (channel?._id && channelOwner?.user_username) {
    return buildChannelRoute(channel, channelOwner);
  }
  if (artwork?.slug) {
    return `/artwork/${artwork.slug}`;
  }
  if (artlist?._id) {
    return `/artlist/${artlist._id}`;
  }
  return null;
}

function OperationalNotificationImage({ artwork }) {
  const artworkImageId = artwork?.artwork_media?.artwork_main_picture?.image_id;

  if (!artworkImageId) {
    return <div className="notification-generic__system-icon" aria-hidden="true">S</div>;
  }

  return (
    <ArtworkImage
      image={artworkImageId}
      imageClassName="notification-generic__artwork-image"
      imageTemplateClassName="notification-generic__artwork-image"
      forceSmaller={100}
      enabledDoubleClick={false}
      alt=""
    />
  );
}

function formatEditionLabel({ editionSizeLabel, editionNumber, editionTotal }) {
  const rawSize = typeof editionSizeLabel === 'string' ? editionSizeLabel.trim() : '';
  const size = rawSize ? `${rawSize.charAt(0).toUpperCase()}${rawSize.slice(1)}` : '';
  const copy = Number.isInteger(editionNumber) && Number.isInteger(editionTotal)
    ? `${editionNumber}/${editionTotal}`
    : '';
  return [size, copy].filter(Boolean).join(' · ');
}

function OperationalNotification({
  type,
  userInteraction: user,
  post,
  comment,
  order,
  review,
  channel,
  channelOwner,
  channelMessage,
  chatMessage,
  artwork,
  artlist,
  artworkTitle,
  salePrice: storedSalePrice,
  saleCurrency: storedSaleCurrency,
  saleVatIncluded: storedSaleVatIncluded,
  editionSizeLabel,
  editionNumber,
  editionTotal,
  elapsed
}) {
  const t = useTranslations('common.notifications.frames');
  const navigate = useNavigate();
  const route = getOperationalNotificationRoute({ type, post, order, channel, channelOwner, artwork, artlist });
  const title = post?.post_container?.post_title
    || artwork?.artwork_about?.artwork_title
    || artlist?.artlist_title
    || artworkTitle
    || '';
  const messageText = buildChannelMessagePreview(channelMessage)
    || chatMessage?.message_text
    || comment?.comment_text
    || '';
  const userName = user?.user_username || t('system_actor');
  const messageKey = OPERATIONAL_NOTIFICATION_KEYS[type];
  const salePrice = formatCurrencyAmount(
    storedSalePrice ?? order?.order_payment?.price,
    storedSaleCurrency ?? order?.order_payment?.currency,
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  ) || '';
  const vatStatus = (storedSaleVatIncluded ?? order?.order_payment?.vat_included) === true
    ? t('artwork_original_sold.vat_included')
    : t('artwork_original_sold.vat_not_included');
  const editionLabel = formatEditionLabel({ editionSizeLabel, editionNumber, editionTotal });
  const message = t(`${messageKey}.message`, {
    userName,
    title,
    channelTitle: channel?.title || '',
    messageText,
    orderNumber: order?.order_number || '',
    rating: review?.valoration || '',
    salePrice,
    vatStatus,
    editionLabel,
  });

  return (
    <div className="notification-generic" onClick={() => route && navigate(route, { state: { from: true } })}>
      <div className="image">
        {user ? (
          <UserProfileImage
            className="notification-generic__profile-image"
            image={user.user_image?.image_id}
            typeOfProfile={resolveNotificationUserType(user)}
            onClick={(event) => {
              event.stopPropagation();
              if (user.user_username) {
                navigate(`/user/${user.user_username}`);
              }
            }}
          />
        ) : (
          <OperationalNotificationImage artwork={artwork} />
        )}
      </div>
      <div className="nf-body">
        <p className="text">
          <span className="msg">{message}</span>
          <span className="elapsed"> {elapsed}</span>
        </p>
      </div>
    </div>
  );
}

function ChannelMemberJoined({ userInteraction: user, channel, channelOwner, elapsed }) {
  const t = useTranslations('common.notifications.frames');
  const navigate = useNavigate();
  const channelRoute = buildChannelRoute(channel, channelOwner);
  const isSubscribersChannel = String(channel?.type || '').toLowerCase() === 'subscribers';
  const messageKey = isSubscribersChannel
    ? 'channel_member_joined.subscribed_message'
    : 'channel_member_joined.message';

  return (
    <div className="notification-generic" onClick={() => channelRoute && navigate(channelRoute, { state: { from: true } })}>
      <div className="image">
        <UserProfileImage
          className="notification-generic__profile-image"
          image={user?.user_image?.image_id}
          typeOfProfile={resolveNotificationUserType(user)}
          onClick={() => navigate(`/user/${user?.user_username}`)}
        />
      </div>
      <div className="nf-body">
        <p className="text">
          <span className="msg">
            <Link
              className="user-link"
              to={`/user/${user?.user_username}`}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/user/${user?.user_username}`);
              }}
              state={{ from: true }}
            >
              {user?.user_username}
            </Link>{' '}
            {t(messageKey, {
              channelTitle: channel?.title || ''
            })}
          </span>
          <span className="elapsed"> {elapsed}</span>
        </p>
      </div>
    </div>
  );
}

function ChannelMessageReply({ userInteraction: user, channel, channelOwner, channelMessage, elapsed }) {
  const t = useTranslations('common.notifications.frames');
  const navigate = useNavigate();
  const channelRoute = buildChannelRoute(channel, channelOwner);
  const replyMessage = buildChannelMessagePreview(channelMessage) || t('channel_message_reply.reply_fallback');

  return (
    <div className="notification-generic" onClick={() => channelRoute && navigate(channelRoute, { state: { from: true } })}>
      <div className="image">
        <UserProfileImage
          className="notification-generic__profile-image"
          image={user?.user_image?.image_id}
          typeOfProfile={resolveNotificationUserType(user)}
          onClick={() => navigate(`/user/${user?.user_username}`)}
        />
      </div>
      <div className="nf-body">
        <p className="text">
          <span className="msg">
            <Link
              className="user-link"
              to={`/user/${user?.user_username}`}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/user/${user?.user_username}`);
              }}
              state={{ from: true }}
            >
              {user?.user_username}
            </Link>{' '}
            {t('channel_message_reply.message', {
              channelTitle: channel?.title || '',
              messageText: replyMessage
            })}
          </span>
          <span className="elapsed"> {elapsed}</span>
        </p>
      </div>
    </div>
  );
}

function SupportOneTime({ userInteraction: user, supportAmount, supportCurrency, elapsed }) {
  const t = useTranslations('common.notifications.frames');
  const navigate = useNavigate();
  const formattedAmount = formatCurrencyAmount(supportAmount, supportCurrency, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) || 'an amount';

  return (
    <div className="notification-generic" onClick={() => navigate(`/user/${user?.user_username}`)}>
      <div className="image">
        <UserProfileImage
          className="notification-generic__profile-image"
          image={user?.user_image?.image_id}
          typeOfProfile={resolveNotificationUserType(user)}
          onClick={() => navigate(`/user/${user?.user_username}`)}
        />
      </div>
      <div className="nf-body">
        <p className="text">
          <span className="msg">
            <Link
              className="user-link"
              to={`/user/${user?.user_username}`}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/user/${user?.user_username}`);
              }}
              state={{ from: true }}
            >
              {user?.user_username}
            </Link>{' '}
            {t('support_one_time.message', { amount: formattedAmount })}
          </span>
          <span className="elapsed"> {elapsed}</span>
        </p>
      </div>
    </div>
  );
}

function SupportRecurring({ userInteraction: user, supportAmount, supportCurrency, elapsed }) {
  const t = useTranslations('common.notifications.frames');
  const navigate = useNavigate();
  const formattedAmount = formatCurrencyAmount(supportAmount, supportCurrency, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) || 'an amount';

  return (
    <div className="notification-generic" onClick={() => navigate(`/user/${user?.user_username}`)}>
      <div className="image">
        <UserProfileImage
          className="notification-generic__profile-image"
          image={user?.user_image?.image_id}
          typeOfProfile={resolveNotificationUserType(user)}
          onClick={() => navigate(`/user/${user?.user_username}`)}
        />
      </div>
      <div className="nf-body">
        <p className="text">
          <span className="msg">
            <Link
              className="user-link"
              to={`/user/${user?.user_username}`}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/user/${user?.user_username}`);
              }}
              state={{ from: true }}
            >
              {user?.user_username}
            </Link>{' '}
            {t('support_recurring.message', { amount: formattedAmount })}
          </span>
          <span className="elapsed"> {elapsed}</span>
        </p>
      </div>
    </div>
  );
}

/* ---------- Mapa de tipos ---------- */

const NotificationTypesConfig = {
  artwork_like: { component: ArtworkLike, requires: ['artwork', 'userInteraction'] },
  artwork_thought_like: { component: ArtworkThoughtLike, requires: ['artwork', 'userInteraction', 'artworkThought'] },
  artwork_thought: { component: ArtworkThought, requires: ['artwork', 'userInteraction', 'artworkThought'] },
  artwork_thought_reply: { component: ThoughtReply, requires: ['artwork', 'userInteraction', 'artworkThought'] },
  artwork_thought_reply_info: { component: ThoughtReplyInfo, requires: ['artwork', 'userInteraction', 'artworkThought', 'userSecondInteraction'] },
  artlist_addition: { component: ArtlistAddition, requires: ['artwork', 'artlist', 'userInteraction'] },
  artwork_thought_reply_like: { component: ThoughtReplyLike, requires: ['artwork', 'userInteraction', 'artworkThought'] },
  artlist_like: { component: ArtlistLike, requires: ['artlist', 'userInteraction'] },
  rating_artwork: { component: RatingArtwork, requires: ['artwork', 'userInteraction'] },
  user_following: { component: UserFollowing, requires: ['userInteraction'] },
  user_mention: { component: UserMention, requires: ['artwork', 'userInteraction', 'artworkThought'] },
  channel_member_joined: { component: ChannelMemberJoined },
  channel_message_reply: { component: ChannelMessageReply },
  support_one_time: { component: SupportOneTime },
  support_recurring: { component: SupportRecurring },
  direct_chat_message: { component: DirectChatMessage },
  post_like: { component: OperationalNotification },
  post_comment: { component: OperationalNotification },
  post_mention: { component: OperationalNotification },
  channel_owner_message: { component: OperationalNotification },
  channel_joined_message: { component: OperationalNotification },
  channel_mention: { component: OperationalNotification },
  order_paid: { component: OperationalNotification },
  shipping_reminder: { component: OperationalNotification },
  order_message: { component: OperationalNotification },
  gallery_review: { component: OperationalNotification },
  artwork_claimed: { component: OperationalNotification },
  order_shipped: { component: OperationalNotification },
  order_delivered: { component: OperationalNotification },
  order_cancelled: { component: OperationalNotification },
  order_refunded: { component: OperationalNotification },
  artwork_original_sold: { component: OperationalNotification },
  limited_edition_sold: { component: OperationalNotification },
  gallery_artwork_marked_sold: { component: OperationalNotification },
  gallery_artwork_added: { component: OperationalNotification },
  gallery_artwork_removed: { component: OperationalNotification },
  follower_artwork_published: { component: OperationalNotification },
  follower_post_published: { component: OperationalNotification },
  follower_artlist_published: { component: OperationalNotification },
  follower_collector_thought_published: { component: OperationalNotification },
  follower_collector_rating_published: { component: OperationalNotification },
  follower_collector_purchased_artwork: { component: OperationalNotification }
};

export default function AlertFrame({ value, showNewNotifications, className = '' }) {
  const rawElapsed = Utils.elapsedTime(new Date(value.createdAt));
  const data = { 
    ...value, 
    elapsed: String(rawElapsed).replace(/\s+/g, '') // "35 m" -> "35m", "1 d" -> "1d"
  };

  const notificationDefinition = NotificationTypesConfig[data.type];
  const NotificationComponent = notificationDefinition?.component || UnknownNotification;
  const isUnavailable = notificationDefinition?.requires
    && !hasRequiredNotificationData(data, notificationDefinition.requires);

  return (
    <div className={`notification ${showNewNotifications && !data.viewed && 'new'} ${className}`}>
      {isUnavailable ? <UnavailableNotification {...data} /> : <NotificationComponent {...data} />}
    </div>
  );
}
